/**
 * ชนิดข้อมูลของ MRP Run (P2-6e) — เขียนตาม Pydantic schema จริงที่เซิร์ฟเวอร์ serve
 * (`apps/api/src/cap_erp_api/modules/planning/schemas.py`, `.../mrp.py`) ตรวจกับ
 * router จริง (`apps/api/src/cap_erp_api/modules/planning/router.py`) ก่อนเขียนไฟล์
 * นี้ — **ไม่ใช่จาก `docs/API_CONTRACT.md` เพียงอย่างเดียว**: เอกสารเคยพิมพ์เส้นทางที่
 * เซิร์ฟเวอร์ไม่ได้ serve มาแล้วครั้งหนึ่งในสัปดาห์นี้ (invariant ของ Work Order นี้)
 *
 * สี่เส้นที่ router จริงเปิดคือทั้งหมดที่กลุ่มนี้มี: `GET`/`POST /v1/mrp-runs`,
 * `GET /v1/mrp-runs/{run_id}` และ `POST /v1/mrp-runs/{run_id}/release` — ไม่มี
 * `PATCH` และไม่มี `DELETE` เลย (D-100/D-101): รันหนึ่งครั้งที่ผิดคือรันใหม่ที่มา
 * แทน ไม่ใช่การแก้รันเดิม และการปล่อย planned order เป็นเอกสารก็ทำครั้งเดียวเท่านั้น
 *
 * **ฝาแฝดของ `lib/forecasts/types.ts` ไม่ใช่ผู้ใช้ของมัน** — แม้ทั้งคู่จะอยู่ในโมดูล
 * สิทธิ์ `PLANNING` เดียวกัน (D-093) ค่าคงที่ที่ควรจะเท่ากัน (เช่น รหัสโมดูล ความยาว
 * รหัสโรงงาน/ชื่อ version) ถูกประกาศซ้ำที่นี่โดยตั้งใจ ผูกกันด้วยค่าที่บังเอิญเท่ากัน
 * ไม่ใช่ import ข้ามโดเมน — แนวเดียวกับที่ `lib/deliveries/types.ts` ไม่ import จาก
 * `lib/sales/types.ts` แม้จะ gate ด้วยโมดูลเดียวกัน
 *
 * สามข้อที่กำหนดทั้งหน้าจอ และทุกข้อมาจาก contract/router ไม่ใช่รสนิยม:
 *
 * 1. **รันคือเอกสาร ไม่ใช่ปุ่มที่เปลี่ยนโลก (D-101)** — `POST /v1/mrp-runs` แค่
 *    คำนวณและเสนอ ส่วนการปล่อยเป็นการกระทำที่แยกต่างหากและจงใจ ชนิดข้างล่างจึงแยก
 *    `MrpRun` (ที่เสนอ) ออกจาก `MrpRunRelease` (ที่ปล่อยแล้ว) เป็นคนละ resource
 * 2. **ตัวเลขทั้งหมดคำนวณโดยเซิร์ฟเวอร์ ไม่ได้ถูกเก็บ** (D-095/D-099 ที่ D-100 สืบทอด
 *    มา) — ทุกจำนวนเดินทางเป็น **สตริง** ที่สเกล `NUMERIC(18, 4)` และห้ามแปลงเป็น
 *    number ที่ไหนเลย
 * 3. **`projected_available_qty` คือยอดที่เข้าสู่ bucket ไม่ใช่ยอดที่ออกจากมัน** —
 *    `docs/API_CONTRACT.md` เขียนไว้ตรง ๆ และเป็นฟิลด์ที่ถูกติดป้ายผิดทางได้ง่ายที่สุด
 *    (ดู `PROJECTED_AVAILABLE_LABEL`/`PROJECTED_AVAILABLE_HINT` ใน `mrpView.ts`)
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — เดียวกับ Forecast/MPS (`PLANNING` ใน
 * `modules/planning/schemas.py`) เพราะ MRP เป็นงานสุดท้ายของสาย Forecast→MPS→MRP
 * ของฝ่ายวางแผน (D-093) */
export const MRP_MODULE = 'PLANNING';

/* ------------------------------------------------------------- สถานะ ----- */

/** รันที่ยังไม่ถูกแทนที่ — **รันเดียวเท่านั้นที่ปล่อยได้** */
export const CURRENT_MRP_RUN_STATUS = 'CURRENT';
/** ถูกแทนที่ด้วยรันใหม่ของคู่ (โรงงาน, MPS) เดียวกัน — **ไม่ได้ถูกลบ** */
export const SUPERSEDED_MRP_RUN_STATUS = 'SUPERSEDED';

/**
 * สองสถานะทั้งหมดที่คอลัมน์นี้มี (D-100) — ไม่มี `DRAFT` และไม่มี `RELEASED`: การ
 * "ปล่อย" ไม่ได้เปลี่ยนสถานะของรันเลย มันสร้างเอกสารแยกต่างหาก (`planned_order_release`)
 * รันหนึ่งจึงปล่อยซ้ำได้โดยที่สถานะยังเป็น `CURRENT` เหมือนเดิม — เซิร์ฟเวอร์เป็นผู้
 * ตัดสินว่าซ้ำแล้วทำอะไร ไม่ใช่หน้าจอนี้
 */
export const MRP_RUN_STATUSES = [
  CURRENT_MRP_RUN_STATUS,
  SUPERSEDED_MRP_RUN_STATUS,
] as const;

export type MrpRunStatus = (typeof MRP_RUN_STATUSES)[number];

/* --------------------------------------------------------- ชนิดของรายการ -- */

/** ประกอบเองได้จากสูตร (มี BOM ปัจจุบันในโรงงานนั้น) */
export const MAKE_ORDER_TYPE = 'MAKE';
/** ต้องซื้อ (ไม่มี BOM ปัจจุบันในโรงงานนั้น — เป็นวัตถุดิบ) */
export const BUY_ORDER_TYPE = 'BUY';

/* ------------------------------------------------------- lot sizing (D-094) -- */

export const LOT_FOR_LOT_PROCEDURE = 'LOT_FOR_LOT';
export const MOQ_PROCEDURE = 'MOQ';

/* --------------------------------------------- เหตุผลที่ถูกงดเว้น (D-102) -- */

/** ชิ้นส่วนนี้อยู่ใต้นโยบาย Pull ที่ ACTIVE — ถูกเติมด้วย Kanban ไม่ใช่ MRP (D-034) */
export const ACTIVE_PULL_POLICY_EXCLUSION_REASON = 'ACTIVE_PULL_POLICY';

/* --------------------------------------------------- เอกสารที่ถูกปล่อย (D-101) -- */

export const PRODUCTION_ORDER_DOCUMENT_TYPE = 'PRODUCTION_ORDER';
export const PURCHASE_REQUISITION_DOCUMENT_TYPE = 'PURCHASE_REQUISITION';

/* ------------------------------------------------------------ กฎ (rule) -- */

/** `422` — ไม่มี MPS ชื่อนี้ในโรงงานนี้เลย */
export const MPS_NOT_FOUND = 'MPS_NOT_FOUND';
/** `422` — MPS ที่ระบุมีอยู่แต่ยังไม่ `RELEASED` (พร้อม `details.current_status`) */
export const MPS_NOT_RELEASED = 'MPS_NOT_RELEASED';
/** `422` — BOM อ้างตัวเองเป็นวงกลม (ส่งต่อมาจาก P2-6a) */
export const BOM_CYCLE_DETECTED = 'BOM_CYCLE_DETECTED';
/** `422` — BOM ลึกเกิน 20 ระดับ (ส่งต่อมาจาก P2-6a) */
export const BOM_EXPLOSION_TOO_DEEP = 'BOM_EXPLOSION_TOO_DEEP';
/** `400` — body มีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ */
export const MRP_RUN_FIELD_IS_SERVER_OWNED = 'MRP_RUN_FIELD_IS_SERVER_OWNED';
/** `403` — โรงงานที่ระบุอยู่นอก `plant_scope` ของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';
/**
 * `422` — รันนี้ไม่ใช่ `CURRENT` อีกแล้ว จึงปล่อยไม่ได้ (พร้อม `details.current_status`
 * และ `details.superseded_by_run_id`)
 *
 * **นี่คือ "คำปฏิเสธของเซิร์ฟเวอร์" ที่หน้าจอต้องเรนเดอร์เมื่อกดปล่อยซ้ำ** — ไม่ใช่
 * ทุกการกดปล่อยซ้ำจะได้คำตอบนี้: รันที่ยังเป็น `CURRENT` อยู่ ปล่อยซ้ำได้แบบ idempotent
 * (เซิร์ฟเวอร์คืนเอกสารชุดเดิม ไม่เขียนอะไรเพิ่ม) แต่รันที่ถูกแทนไปแล้วระหว่างนั้นจะได้
 * `422` นี้แทน — หน้าจอไม่เสนอปุ่มปล่อยซ้ำอีกหลังจากได้คำตอบนี้ครั้งหนึ่ง
 */
export const MRP_RUN_NOT_CURRENT = 'MRP_RUN_NOT_CURRENT';

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** ความยาวเดียวกับคอลัมน์ `plant.plant_code` — ค่าเดียวกับที่หน้าจอ Planning อื่นใช้ */
export const PLANT_CODE_MAX_LENGTH = 20;
/** `version_label` ≤30 (`models.VERSION_LABEL_LENGTH`) — ชื่อแผนที่ฝ่ายวางแผนตั้งเอง */
export const VERSION_LABEL_MAX_LENGTH = 30;

/* ------------------------------------------------------------- resource --- */

/**
 * หนึ่ง planned order — สี่ตัวเลขที่ตอบว่า "ทำไมถึงสั่งเท่านี้" (D-100)
 *
 * ทุกจำนวนเป็น **สตริง** เสมอ (`NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้
 * ครบ) และไม่มีตัวไหนถูกคำนวณซ้ำที่นี่ — เซิร์ฟเวอร์เป็นผู้คำนวณและหน้าจอแสดงเฉยๆ
 * (D-095/D-099/D-100)
 */
export interface PlannedOrder {
  readonly line_no: number;
  readonly product_id: string;
  readonly product_code: string;
  /** `YYYY-MM-DD` ล้วน — วันที่ต้องการ ไม่ใช่ instant */
  readonly required_date: string;
  /** ความต้องการรวมของวันนั้น */
  readonly gross_requirement_qty: string;
  /**
   * ยอดที่ **เข้า** bucket นี้ (หลังหัก safety stock แล้ว) — **ไม่ใช่ยอดที่ออก**
   * ดู `PROJECTED_AVAILABLE_LABEL` ใน `mrpView.ts` ก่อนแสดงค่านี้บนหน้าจอ
   */
  readonly projected_available_qty: string;
  /** ส่วนที่ขาด — เป็น 0 ไม่ได้ (bucket ที่ขาด 0 ไม่มี planned order เกิดขึ้นเลย) */
  readonly net_requirement_qty: string;
  /** ส่วนที่ขาด ผ่านนโยบาย lot sizing ของโรงงานนี้แล้ว (D-094) */
  readonly order_qty: string;
  /** `MAKE` หรือ `BUY` */
  readonly order_type: string;
  /** `LOT_FOR_LOT` หรือ `MOQ` — ไม่เคยเป็น `null` */
  readonly lot_size_procedure_used: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * หนึ่งชิ้นส่วนที่รันนี้ **จงใจไม่สั่ง** เพราะอยู่ใต้นโยบาย Pull ที่ ACTIVE (D-102 ข้อ
 * 8a) — ครึ่งที่มองไม่เห็นของผลลัพธ์ MRP ถ้าไม่มีแถวนี้ ผู้วางแผนจะเห็นแค่ชิ้นส่วน
 * หายไปจาก `planned_orders` โดยไม่รู้ว่าทำไม
 */
export interface MrpRunExclusion {
  readonly product_id: string;
  readonly product_code: string;
  /** วันนี้มีค่าเดียวคือ `ACTIVE_PULL_POLICY` แต่เก็บเป็นสตริงเปิดไว้ เหมือน `status`
   * ของ resource อื่นในระบบนี้ — token ใหม่ในอนาคตต้องไม่ทำให้ parser เก่าพัง */
  readonly reason: string;
  readonly policy_id: string;
  readonly created_at: string;
}

/**
 * หัวรันหนึ่งใบ **โดยไม่มี `planned_orders`/`exclusions`** — รูปของแต่ละ item ใน
 * `GET /v1/mrp-runs`
 *
 * ไม่มีสองคีย์นั้นอยู่ในชนิดนี้เลยโดยตั้งใจ: contract ตัดมันออกจากรายการเพราะรันหนึ่ง
 * มี order ได้เป็นร้อย (หนึ่งรายการต่อชิ้นส่วนต่อวัน) หน้าจอที่ต้องการ order ต้องเรียก
 * `fetchMrpRun()` ของรันนั้นเอง และ TypeScript เป็นผู้พิสูจน์ว่าไม่มีใครเผลออ่านมันจาก
 * แถวในรายการ
 */
export interface MrpRunSummary {
  readonly run_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly mps_id: string;
  /** label ที่ **resolve แล้ว** — แผนที่รันนี้อ่านมาจริง ไม่ใช่สิ่งที่ผู้เรียกพิมพ์ */
  readonly mps_version_label: string;
  readonly status: string;
  /** instant — เมื่อไรที่เลขถูกคำนวณ ต่างจาก `required_date` ของแต่ละบรรทัดซึ่งเป็นวัน */
  readonly run_at: string;
  readonly created_by_employee_id: string;
  /** จำนวนออเดอร์ที่รันนี้ผลิต — `0` เป็นคำตอบที่ถูกต้อง (สต๊อกครอบคลุมทุก bucket) */
  readonly planned_order_count: number;
  /** รันที่มาแทนที่รันนี้ — `null` บนรันที่ยังเป็น `CURRENT` */
  readonly superseded_by_run_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * รันหนึ่งใบเต็ม ๆ **พร้อม planned orders และ exclusions** — รูปของ `GET/{run_id}`
 * และของ `201` ที่ `POST` คืนมา
 */
export interface MrpRun extends MrpRunSummary {
  readonly planned_orders: readonly PlannedOrder[];
  readonly exclusions: readonly MrpRunExclusion[];
}

/** `GET /v1/mrp-runs` — envelope มาตรฐานของทุกกลุ่มในระบบนี้ */
export interface MrpRunListPage {
  readonly items: readonly MrpRunSummary[];
  readonly next_cursor: string | null;
}

/**
 * หนึ่ง planned order ที่กลายเป็นเอกสารจริงแล้ว (D-101 ข้อ 3-5)
 *
 * ครึ่งของ planned order (`line_no`, `product_code`, `order_qty`, `order_type`) คือ
 * สิ่งที่ผู้วางแผน**จำได้** เพราะกำลังดูรันอยู่ ส่วนครึ่งของเอกสาร (`document_type`,
 * `document_id`, `document_number`, `document_status`) คือสิ่งที่เขา**ต้องไปทำต่อ** —
 * คำตอบที่มีแค่ครึ่งแรกจะทำให้ "บรรทัดนี้ปล่อยแล้วหรือยัง" กลายเป็นคำถามที่ตอบไม่ได้
 */
export interface ReleasedDocument {
  readonly line_no: number;
  readonly product_id: string;
  readonly product_code: string;
  readonly required_date: string;
  readonly order_qty: string;
  readonly order_type: string;
  /** `PRODUCTION_ORDER` หรือ `PURCHASE_REQUISITION` */
  readonly document_type: string;
  readonly document_id: string;
  /** `order_no` หรือ `pr_number` — server-assigned ทั้งคู่ */
  readonly document_number: string;
  /** `DRAFT` เสมอบนทั้งสองชนิด (D-010) — ไม่มีเส้นทางใดปล่อยแล้วได้สถานะอื่น */
  readonly document_status: string;
  readonly released_by_employee_id: string;
  readonly released_at: string;
}

/**
 * `POST /v1/mrp-runs/{run_id}/release` — รัน + สิ่งที่มันผลิตออกมา
 *
 * **รูปเดียวกันตอบทั้งครั้งแรกและครั้งที่ปล่อยซ้ำ** เพราะการปล่อยซ้ำของรันที่ยังเป็น
 * `CURRENT` ไม่สร้างอะไรใหม่และคืนเอกสารชุดเดิม (ดูหมายเหตุของ `MRP_RUN_NOT_CURRENT`
 * ด้านบนสำหรับกรณีที่ต่างออกไป)
 */
export interface MrpRunRelease {
  readonly run_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  /** สถานะของรัน ณ ตอนปล่อย — เส้นนี้ไปถึงได้เฉพาะตอนที่ยังเป็น `CURRENT` เท่านั้น */
  readonly status: string;
  /** จำนวนเอกสารที่ปล่อยออกมา — `0` เป็นคำตอบที่ถูกต้อง (รันไม่มี planned order เลย) */
  readonly released_document_count: number;
  readonly documents: readonly ReleasedDocument[];
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/mrp-runs` — สองฟิลด์ตามที่ contract กำหนด ไม่มีคีย์ที่สาม
 *
 * **ไม่มี `run_at`**: รันเสมอ ณ ตอนนี้ กับสต๊อกที่ ledger ถืออยู่ตอนนี้ — ไม่มีช่องให้
 * เลือกวันอื่นเพราะรันที่อ้างอิงเวลาอื่นได้จะเป็นแผนที่ไม่มีใครสร้างซ้ำได้อีก
 */
export interface CreateMrpRunBody {
  readonly plant_code: string;
  readonly mps_version_label: string;
}
